import React, { useEffect, useState, useContext, useRef } from "react";
import axios from "axios";
import "./Cart.css";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* =========================
    CART API FUNCTIONS
========================= */

export const addToCart = async (
  customer_id,
  product_id,
  quantity = 1,
  setCartCount,
  incrementBy = 1,
) => {
  const formData = new FormData();
  formData.append("customer_id", customer_id);
  formData.append("product_id", product_id);
  formData.append("quantity", quantity);

  const res = await axios.post(
    `${BASE_URL}customer_cart_details_insert`,
    formData,
  );

  // ✅ FIX: Only increment badge if it's a NEW product (quantity was 0 before)
  // The incrementBy should be 1 only when adding a new product
  if (res.data.api_success && setCartCount && quantity === 1) {
    setCartCount((prev) => prev + 1);
  }

  return res.data;
};

const Cart = () => {
  const handleCartBuyNow = () => {
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to continue");
      return;
    }

    if (cartItems.length === 0) {
      toast.warning("Your cart is empty");
      return;
    }

    const cartProduct = {
      product_id: "CART",
      product_name: "Cart Items",
      product_descriptions: "Multiple items from cart",
      price: totalAmount,
      images: cartItems.map((item) => item.product_image),
      cartItems,
    };

    navigate("/payment", {
      state: {
        product: cartProduct,
        quantity: 1,
        unitPrice: totalAmount,
        totalPrice: totalAmount,
      },
    });
  };

  const { getCustomerData, isAuthenticated, setCartCount } =
    useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;

  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFetchingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  /* =========================
     FETCH CART ITEMS
  ========================== */
  const fetchCart = async () => {
    if (!customer_id) {
      setLoading(false);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("customer_id", customer_id);

      const res = await axios.post(
        `${BASE_URL}customer_cart_details_list`,
        formData,
      );

      if (res.data.api_success) {
        const normalizedCart = res.data.cart_items.map((item) => {
          const selling_price = parseFloat(item.selling_price) || 0;
          const quantity = parseInt(item.quantity, 10) || 0;

          return {
            ...item,
            selling_price,
            quantity,
          };
        });

        setCartItems(normalizedCart);
        
        // ✅ FIX: Set badge to number of UNIQUE PRODUCTS, not total quantity
        const uniqueProductCount = normalizedCart.length;
        setCartCount(uniqueProductCount);
        
      } else {
        setCartItems([]);
      }
    } catch (error) {
      setCartItems([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  /* =========================
     PRODUCT NAVIGATION
  ========================== */
  const handleProductClick = (product_id) => {
    navigate(`/product-details/${product_id}`);
  };

  /* =========================
     INCREMENT QTY
  ========================== */
  const incrementQty = async (item) => {
    const currentQty = parseInt(item.quantity, 10) || 0;
    const newQty = currentQty + 1;

    setCartItems((prev) =>
      prev.map((i) =>
        i.product_id === item.product_id
          ? { ...i, quantity: Number(i.quantity) + 1 }
          : i,
      ),
    );

    try {
      const formData = new FormData();
      formData.append("customer_id", customer_id);
      formData.append("product_id", item.product_id);
      formData.append("quantity", newQty.toString());

      const response = await axios.post(
        `${BASE_URL}customer_cart_details_update`,
        formData,
      );

      if (!response.data.api_success) {
        await fetchCart();
      }
      // ✅ NO badge update needed - still same number of unique products
    } catch (error) {
      await fetchCart();
    }
  };

  /* =========================
     DECREMENT QTY
  ========================== */
  const decrementQty = async (item) => {
    const currentQty = parseInt(item.quantity, 10) || 0;

    if (currentQty <= 1) return;

    const newQty = currentQty - 1;

    setCartItems((prev) =>
      prev.map((i) =>
        i.product_id === item.product_id
          ? { ...i, quantity: Number(i.quantity) - 1 }
          : i,
      ),
    );

    try {
      const formData = new FormData();
      formData.append("customer_id", customer_id);
      formData.append("product_id", item.product_id);
      formData.append("quantity", newQty.toString());

      const response = await axios.post(
        `${BASE_URL}customer_cart_details_update`,
        formData,
      );

      if (!response.data.api_success) {
        await fetchCart();
      }
      // ✅ NO badge update needed - still same number of unique products
    } catch (error) {
      await fetchCart();
    }
  };

  /* =========================
     REMOVE ITEM
  ========================== */
  const removeItem = async (item) => {
    try {
      const formData = new FormData();
      formData.append("customer_id", customer_id);
      formData.append("product_id", item.product_id);

      const response = await axios.post(
        `${BASE_URL}customer_cart_details_delete`,
        formData,
      );

      if (response.data.api_success) {
        setCartItems((prev) => {
          const updatedCart = prev.filter(
            (i) => i.product_id !== item.product_id,
          );

          // ✅ FIX: Update badge to number of unique products remaining
          setCartCount(updatedCart.length);

          return updatedCart;
        });

        toast.info("Item removed from cart");
      } else {
        toast.error("Failed to remove item");
        await fetchCart();
      }
    } catch (error) {
      toast.error("Failed to remove item");
      await fetchCart();
    }
  };

  /* =========================
     CALCULATIONS
  ========================== */
  const totalItems = cartItems.reduce(
    (sum, item) => sum + parseInt(item.quantity, 10),
    0,
  );

  const totalAmount = cartItems.reduce((sum, item) => {
    const sellingPrice = parseFloat(item.selling_price) || 0;
    const qty = parseInt(item.quantity, 10) || 0;
    return sum + sellingPrice * qty;
  }, 0);

  /* =========================
     EFFECT
  ========================== */
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    if (!isAuthenticated) {
      toast.warning("Please login to view cart");
      setLoading(false);
      return;
    }

    if (customer_id) {
      hasInitializedRef.current = true;
      fetchCart();
    }
  }, []);

  useEffect(() => {
    if (!hasInitializedRef.current) return;

    if (!isAuthenticated || !customer_id) {
      setCartItems([]);
      setLoading(false);
    }
  }, [isAuthenticated, customer_id]);

  if (loading) {
    return <p style={{ textAlign: "center" }}>Loading cart...</p>;
  }

  return (
    <div className="cart-container">
      <h2 className="cart-heading">My Cart</h2>

      <div className="cart-items">
        {cartItems.length === 0 && (
          <p className="empty-text">Your cart is empty</p>
        )}

        {cartItems.map((item) => {
          const displayPrice = parseFloat(item.selling_price) || 0;
          const displayQty = parseInt(item.quantity, 10) || 0;

          return (
            <div className="cart-item" key={item.product_id}>
              <div
                className="cart-click-area"
                onClick={() => handleProductClick(item.product_id)}
              >
                <img
                  className="item-img"
                  src={item.product_image}
                  alt={item.product_name}
                />

                <div className="item-details">
                  <p className="item-name">{item.product_name}</p>
                  <p className="item-price">
                    ₹{(displayPrice * displayQty).toFixed(0)}
                  </p>
                </div>
              </div>

              <div className="cart-item-bottom">
                <div className="qty-controls">
                  <button onClick={() => decrementQty(item)}>-</button>
                  <span>{displayQty}</span>
                  <button className="plus" onClick={() => incrementQty(item)}>
                    +
                  </button>
                </div>

                <div className="btn-group">
                  <button
                    className="remove-btn"
                    onClick={() => removeItem(item)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {cartItems.length > 0 && (
        <div className="cart-total-bar">
          <div className="cart-total-left">
            <span className="total-items-count">{totalItems} items</span>
            <span className="total-amount">₹{totalAmount.toFixed(0)}</span>
          </div>

          <button className="total-buy-btn" onClick={handleCartBuyNow}>
            Buy Now
          </button>
        </div>
      )}
    </div>
  );
};

export default Cart;